import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Button,
  Paper,
  Chip
} from '@mui/material';
import {
  Code as CodeIcon,
  Speed as SpeedIcon,
  Search as SearchIcon,
  Security as SecurityIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckIcon,
  Bolt as BoltIcon,
  ImageSearch as ImageIcon,
  Cached as CacheIcon,
  Architecture as ArchitectureIcon,
  Api as ApiIcon,
  Token as TokenIcon,
  PhoneAndroid as MobileIcon,
  Lock as LockIcon,
  Shield as ShieldIcon,
  VerifiedUser as VerifiedIcon,
  Update as UpdateIcon,
  Backup as BackupIcon
} from '@mui/icons-material';
import { useState } from 'react';

const TechnicalFeaturesPage = () => {
  const [hoveredCard, setHoveredCard] = useState(null);

  // Teknoloji Stack
  const techStack = [
    {
      icon: <CodeIcon sx={{ fontSize: 50 }} />,
      title: "Next.js 14",
      subtitle: "Frontend Framework",
      description: "Modern React framework ile SSR, SSG ve ISR desteği",
      color: "#000000",
      gradient: "linear-gradient(135deg, #000000 0%, #334155 100%)",
      features: [
        "App Router mimarisi",
        "Server Components",
        "API Routes",
        "Automatic Code Splitting"
      ]
    },
    {
      icon: <ArchitectureIcon sx={{ fontSize: 50 }} />,
      title: "Material-UI (MUI)",
      subtitle: "UI Framework",
      description: "Google'ın Material Design prensipleri ile modern arayüz",
      color: "#1e40af",
      gradient: "linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)",
      features: [
        "Hazır component kütüphanesi",
        "Responsive grid sistemi",
        "Tema özelleştirme",
        "Accessibility desteği"
      ]
    },
    {
      icon: <ApiIcon sx={{ fontSize: 50 }} />,
      title: "GraphQL (Apollo)",
      subtitle: "API Layer",
      description: "Esnek ve performanslı veri yönetimi",
      color: "#dc2626",
      gradient: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
      features: [
        "Type-safe API",
        "Real-time subscriptions",
        "Caching stratejileri",
        "Optimistic UI updates"
      ]
    },
    {
      icon: <TokenIcon sx={{ fontSize: 50 }} />,
      title: "JWT Authentication",
      subtitle: "Güvenli Kimlik Doğrulama",
      description: "Token tabanlı güvenli oturum yönetimi",
      color: "#059669",
      gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
      features: [
        "Stateless authentication",
        "Refresh token mekanizması",
        "Role-based access control",
        "Secure token storage"
      ]
    },
    {
      icon: <MobileIcon sx={{ fontSize: 50 }} />,
      title: "Mobile-First Design",
      subtitle: "Responsive Yapı",
      description: "Her cihazda mükemmel kullanıcı deneyimi",
      color: "#7c3aed",
      gradient: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
      features: [
        "Progressive Web App (PWA)",
        "Touch optimizasyonu",
        "Offline çalışma desteği",
        "Native app deneyimi"
      ]
    },
    {
      icon: <ArchitectureIcon sx={{ fontSize: 50 }} />,
      title: "Nx Monorepo",
      subtitle: "Admin Panel",
      description: "Modüler ve ölçeklenebilir admin yapısı",
      color: "#ea580c",
      gradient: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
      features: [
        "Modüler mimari",
        "Code sharing",
        "Build optimizasyonu",
        "Developer experience"
      ]
    }
  ];

  // Performans Özellikleri
  const performanceFeatures = [
    {
      icon: <BoltIcon sx={{ fontSize: 45 }} />,
      title: "Server-Side Rendering",
      description: "İlk yükleme hızı ve SEO optimizasyonu",
      color: "#1e40af",
      metric: "SSR"
    },
    {
      icon: <StorageIcon sx={{ fontSize: 45 }} />,
      title: "Static Site Generation",
      description: "Statik sayfa oluşturma ve CDN desteği",
      color: "#dc2626",
      metric: "SSG"
    },
    {
      icon: <UpdateIcon sx={{ fontSize: 45 }} />,
      title: "Incremental Static Regeneration",
      description: "Dinamik içerik güncellemesi",
      color: "#059669",
      metric: "ISR"
    },
    {
      icon: <ImageIcon sx={{ fontSize: 45 }} />,
      title: "Görsel Optimizasyonu",
      description: "Otomatik resim sıkıştırma ve WebP desteği",
      color: "#7c3aed",
      metric: "WebP"
    },
    {
      icon: <CacheIcon sx={{ fontSize: 45 }} />,
      title: "Akıllı Önbellekleme",
      description: "CDN ve browser cache stratejileri",
      color: "#ea580c",
      metric: "Cache"
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 45 }} />,
      title: "Code Splitting",
      description: "Modüler kod yükleme ve lazy loading",
      color: "#0891b2",
      metric: "Lazy"
    }
  ];

  // SEO & Analitik
  const seoFeatures = [
    {
      icon: <SearchIcon sx={{ fontSize: 45 }} />,
      title: "Gelişmiş SEO",
      description: "Meta tag yönetimi ve arama motoru optimizasyonu",
      items: [
        "Dinamik meta tags",
        "Canonical URLs",
        "Breadcrumb markup",
        "Alt text yönetimi"
      ]
    },
    {
      icon: <AnalyticsIcon sx={{ fontSize: 45 }} />,
      title: "Analytics & Tracking",
      description: "Detaylı ziyaretçi ve içerik analitiği",
      items: [
        "Google Analytics 4",
        "Custom event tracking",
        "Conversion tracking",
        "Heatmap entegrasyonu"
      ]
    }
  ];

  // Güvenlik Özellikleri
  const securityFeatures = [
    {
      icon: <ShieldIcon sx={{ fontSize: 40 }} />,
      title: "HTTPS & SSL",
      description: "Şifreli bağlantı desteği",
      color: "#1e40af"
    },
    {
      icon: <LockIcon sx={{ fontSize: 40 }} />,
      title: "XSS Koruması",
      description: "Cross-site scripting önleme",
      color: "#dc2626"
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40 }} />,
      title: "CSRF Koruması",
      description: "Cross-site request forgery koruması",
      color: "#059669"
    },
    {
      icon: <VerifiedIcon sx={{ fontSize: 40 }} />,
      title: "2FA Hazır",
      description: "İki faktörlü kimlik doğrulama",
      color: "#7c3aed"
    },
    {
      icon: <BackupIcon sx={{ fontSize: 40 }} />,
      title: "Aktivite Logları",
      description: "Tüm işlemlerin kaydı",
      color: "#ea580c"
    },
    {
      icon: <TokenIcon sx={{ fontSize: 40 }} />,
      title: "Veri Şifreleme",
      description: "Hassas veri koruması",
      color: "#0891b2"
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: `
            linear-gradient(
              135deg,
              #0f172a 0%,
              #1e293b 50%,
              #334155 100%
            )
          `,
          position: 'relative',
          overflow: 'hidden',
          color: 'white',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              radial-gradient(circle at 20% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(139, 92, 246, 0.15) 0%, transparent 50%)
            `,
            pointerEvents: 'none'
          }
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 100,
                height: 100,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                mb: 4,
                mx: 'auto'
              }}
            >
              <CodeIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>

            <Typography
              variant="overline"
              sx={{
                fontSize: '0.9rem',
                fontWeight: 900,
                letterSpacing: '0.15em',
                color: '#3b82f6',
                mb: 2,
                display: 'block',
                textTransform: 'uppercase'
              }}
            >
              TEKNİK ÖZELLİKLER
            </Typography>

            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '4.5rem' },
                fontWeight: 800,
                mb: 4,
                color: 'white',
                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
                lineHeight: 1.1
              }}
            >
              Modern Teknoloji Stack
            </Typography>

            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.2rem', md: '1.5rem' },
                color: 'rgba(255,255,255,0.8)',
                maxWidth: 800,
                mx: 'auto',
                lineHeight: 1.6,
                fontWeight: 400,
                mb: 6
              }}
            >
              Next.js 14, GraphQL, JWT Authentication ve daha fazlası ile 
              enterprise seviye performans ve güvenlik
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                  color: 'white',
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(59, 130, 246, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Teknik Dokümantasyon
              </Button>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: 'white',
                  color: 'white',
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  borderRadius: 2,
                  borderWidth: 2,
                  textTransform: 'none',
                  '&:hover': {
                    borderWidth: 2,
                    background: 'rgba(255, 255, 255, 0.1)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                API Referansı
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Teknoloji Stack */}
      <Box sx={{ py: { xs: 10, md: 16 }, background: '#f8fafc' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                background: 'linear-gradient(135deg, #1e293b 0%, #1e40af 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Teknoloji Stack
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#64748b',
                maxWidth: 700,
                mx: 'auto',
                lineHeight: 1.6
              }}
            >
              En güncel ve güçlü teknolojiler
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {techStack.map((tech, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Paper
                  elevation={0}
                  onMouseEnter={() => setHoveredCard(index)}
                  onMouseLeave={() => setHoveredCard(null)}
                  sx={{
                    p: 5,
                    height: '100%',
                    background: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: 4,
                    cursor: 'pointer',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 4,
                      background: tech.gradient,
                      transform: hoveredCard === index ? 'scaleX(1)' : 'scaleX(0)',
                      transformOrigin: 'left',
                      transition: 'transform 0.3s ease'
                    },
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: `0 20px 40px ${tech.color}20`,
                      borderColor: tech.color
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 80,
                      height: 80,
                      borderRadius: 3,
                      background: `${tech.color}15`,
                      color: tech.color,
                      mb: 3,
                      transition: 'all 0.4s ease',
                      transform: hoveredCard === index ? 'scale(1.1) rotate(5deg)' : 'scale(1)'
                    }}
                  >
                    {tech.icon}
                  </Box>

                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: '#1e293b',
                      fontSize: '1.5rem'
                    }}
                  >
                    {tech.title}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      mb: 3,
                      background: tech.gradient,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      fontSize: '1.1rem'
                    }}
                  >
                    {tech.subtitle}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      color: '#64748b',
                      lineHeight: 1.7,
                      mb: 3
                    }}
                  >
                    {tech.description}
                  </Typography>

                  <List dense>
                    {tech.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon sx={{ color: tech.color, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{
                            fontSize: '0.95rem',
                            color: '#475569',
                            fontWeight: 500
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Performans Optimizasyonu */}
      <Box sx={{ py: { xs: 10, md: 16 }, background: 'white' }}>
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 800,
                mb: 3,
                background: 'linear-gradient(135deg, #dc2626 0%, #ea580c 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Performans Optimizasyonu
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#64748b',
                maxWidth: 700,
                mx: 'auto'
              }}
            >
              Yıldırım hızında yükleme süreleri
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {performanceFeatures.map((feature, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    p: 4,
                    height: '100%',
                    background: `${feature.color}05`,
                    border: `2px solid ${feature.color}20`,
                    borderRadius: 3,
                    textAlign: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: `${feature.color}10`,
                      borderColor: feature.color,
                      transform: 'translateY(-5px)',
                      boxShadow: `0 10px 30px ${feature.color}30`
                    }
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 70,
                      height: 70,
                      borderRadius: 2,
                      background: `${feature.color}20`,
                      color: feature.color,
                      mb: 3
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 2, lineHeight: 1.6 }}>
                    {feature.description}
                  </Typography>
                  <Chip
                    label={feature.metric}
                    sx={{
                      background: feature.color,
                      color: 'white',
                      fontWeight: 600
                    }}
                  />
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* SEO & Güvenlik */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: 'linear-gradient(135deg, #1e40af 0%, #7c3aed 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="xl">
          <Grid container spacing={8}>
            {/* SEO Özellikleri */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 800,
                  mb: 6
                }}
              >
                SEO & Analitik
              </Typography>
              {seoFeatures.map((feature, index) => (
                <Box key={index} sx={{ mb: 5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        background: 'rgba(255, 255, 255, 0.2)',
                        mr: 3
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                  <List dense>
                    {feature.items.map((item, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon sx={{ color: 'white' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              ))}
            </Grid>

            {/* Güvenlik Özellikleri */}
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: { xs: '2rem', md: '2.5rem' },
                  fontWeight: 800,
                  mb: 6
                }}
              >
                Güvenlik Özellikleri
              </Typography>
              <Grid container spacing={3}>
                {securityFeatures.map((feature, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Card
                      sx={{
                        p: 3,
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        borderRadius: 2,
                        textAlign: 'center',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          background: 'rgba(255, 255, 255, 0.15)',
                          transform: 'translateY(-3px)'
                        }
                      }}
                    >
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 60,
                          height: 60,
                          borderRadius: 2,
                          background: `${feature.color}40`,
                          color: feature.color,
                          mb: 2
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: 'white' }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                        {feature.description}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 10, md: 16 },
          background: '#0f172a',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '2.5rem', md: '3.5rem' },
              fontWeight: 800,
              mb: 3
            }}
          >
            Enterprise Seviye Teknoloji
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 6,
              opacity: 0.9,
              lineHeight: 1.6
            }}
          >
            Modern teknoloji stack ile güçlü, hızlı ve güvenli bir platform
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
              color: 'white',
              px: 6,
              py: 2.5,
              fontSize: '1.2rem',
              fontWeight: 700,
              borderRadius: 2,
              textTransform: 'none',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 10px 30px rgba(59, 130, 246, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Teknik Detayları İnceleyin
          </Button>
        </Container>
      </Box>
    </Box>
  );
};

export default TechnicalFeaturesPage;

